import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Select, 
  Switch, 
  InputNumber, 
  Button, 
  Card, 
  Typography, 
  message, 
  Spin, 
  Space,
  Divider 
} from 'antd';
import { ArrowLeft, Save, Import } from 'lucide-react';
import styled from '@emotion/styled';
import useProductGroupOpenApi, { 
  ProductGroupDto, 
  ProductGroupUpdateRequest, 
  ProductGroupType 
} from 'apiClients/useProductGroupOpenApiClient';
import { getProductGroupTypeOptions, getDescriptionFromLocale } from 'utility/helper';
import { useLocaleStore } from 'stores/localeStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ProductSelectView, { SelectedViewProduct } from './ProductSelectView';
import ProductGroupAssignSkuDialog from './ProductGroupAssignSkuDialog';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const StyledCard = styled(Card)`
  flex: 1;
  margin: 0;
`;

const Container = styled.div`
  display: flex;
  gap: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  min-width: 0; /* Prevent flex item overflow */
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0; /* Prevent flex item overflow */
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const TranslationSection = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

interface ProductGroupEditViewProps {
  // You can add props here if needed when using as a component
}

const ProductGroupEditView: React.FC<ProductGroupEditViewProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedViewProduct[]>([]);
  const [skuDialogVisible, setSkuDialogVisible] = useState(false);
  const { getProductGroup, updateProductGroup, assignProductsByIds } = useProductGroupOpenApi();
  const { languages } = useLocaleStore();
  const queryClient = useQueryClient();

  const productGroupTypes = getProductGroupTypeOptions();

  // Fetch product group data
  const { data: productGroup, isLoading, error } = useQuery({
    queryKey: ['product-group', id],
    queryFn: () => getProductGroup(Number(id)),
    enabled: !!id && !isNaN(Number(id)),
  });

  // Initialize form when data is loaded
  useEffect(() => {
    if (productGroup) {
      // Map translations to form structure
      const translationFields: Record<string, any> = {};
      productGroup.translations?.forEach(translation => {
        if (translation.languageId) {
          translationFields[`name_${translation.languageId}`] = translation.name;
          translationFields[`description_${translation.languageId}`] = translation.description;
        }
      });

      form.setFieldsValue({
        type: productGroup.type,
        sortOrder: productGroup.sortOrder || 0,
        isActive: productGroup.isActive ?? true,
        internalCode: productGroup.internalCode,
        ...translationFields,
      });

      setSelectedProducts(productGroup.products?.map(p => ({
        id: p.productId || 0,
        name: p.productName || '',
        sortOrder: p.sortOrder || 0,
        mainItemNo: p.mainItemNo || '',
        imageUrl: p.imageUrl || ''
      })) || []);
    }
  }, [productGroup, form]);

  const handleSubmit = async (values: any) => {
    if (!id || !productGroup) return;

    setLoading(true);
    try {
      // Build translations array from form values
      const translations = languages.map(language => ({
        name: values[`name_${language.id}`] || null,
        description: values[`description_${language.id}`] || null,
        languageId: language.id,
      })).filter(translation => translation.name || translation.description);

      const updateRequest: ProductGroupUpdateRequest = {
        type: values.type,
        sortOrder: values.sortOrder,
        isActive: values.isActive,
        internalCode: values.internalCode || null,
        translations: translations.length > 0 ? translations : null,
      };

      const result = await updateProductGroup(Number(id), updateRequest);
      
      if (result) {
        // If there are selected products, assign them to the product group
        if (selectedProducts.length > 0) {
          // Sort products by their sortOrder to maintain intended order
          const sortedProducts = [...selectedProducts].sort((a, b) => a.sortOrder - b.sortOrder);
          const productIds = sortedProducts.map(p => p.id);
          
          await assignProductsByIds(Number(id), productIds);
          message.success(`Produktgrupp uppdaterad och ${selectedProducts.length} produkter tilldelade i önskad ordning`);
        } else {
          message.success('Produktgrupp uppdaterad framgångsrikt');
        }
        
        // Invalidate related queries
        queryClient.invalidateQueries({ 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0]?.toString().startsWith('product-groups-lang-') ||
             query.queryKey[0] === 'product-group')
        });
        // Navigate back to list or stay on edit page
        // navigate('/productGroups');
      } else {
        message.error('Fel vid uppdatering av produktgrupp');
      }
    } catch (error) {
      message.error('Ett oväntat fel inträffade');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/productGroups');
  };

  const handleSkuImportSuccess = () => {
    // Refetch the product group to get updated product list
    queryClient.invalidateQueries({ 
      queryKey: ['product-group', id]
    });
    
    // Also invalidate other related queries
    queryClient.invalidateQueries({ 
      predicate: (query) => 
        Array.isArray(query.queryKey) && 
        (query.queryKey[0]?.toString().startsWith('product-groups-lang-') ||
         query.queryKey[0] === 'product-group')
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !productGroup) {
    return (
      <StyledCard>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Title level={4}>Produktgrupp hittades inte</Title>
          <Button onClick={handleBack}>Tillbaka till listan</Button>
        </div>
      </StyledCard>
    );
  }

  return (
    <>
      <Container>
        <LeftColumn>
          <StyledCard title={`Redigera produktgrupp: ${productGroup.name || productGroup.id}`}>
            <HeaderSection>
              <Button
                style={{
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                }} 
                icon={<ArrowLeft size={16} />} 
                onClick={handleBack}
                type="text"
              >
                Tillbaka
              </Button>
            </HeaderSection>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                isActive: true,
                sortOrder: 0,
              }}
            >
              <Form.Item
                label="Typ"
                name="type"
                rules={[{ required: true, message: 'Typ är obligatorisk' }]}
              >
                <Select placeholder="Välj produktgruppstyp">
                  {productGroupTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Intern kod"
                name="internalCode"
              >
                <Input placeholder="Ange intern kod (valfritt)" />
              </Form.Item>

              <Form.Item
                label="Sorteringsordning"
                name="sortOrder"
                rules={[{ required: true, message: 'Sorteringsordning är obligatorisk' }]}
              >
                <InputNumber 
                  min={0} 
                  placeholder="Ange sorteringsordning"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="Aktiv"
                name="isActive"
                valuePropName="checked"
              >
                <Switch checkedChildren="Aktiv" unCheckedChildren="Inaktiv" />
              </Form.Item>

              <Divider>Översättningar</Divider>

              {languages.map((language) => (
                <TranslationSection key={language.id}>
                  <Title level={5}>{getDescriptionFromLocale(language.locale)} ({language.locale})</Title>
                  
                  <Form.Item
                    label="Namn"
                    name={`name_${language.id}`}
                    rules={[
                      { max: 100, message: 'Namn får inte vara längre än 100 tecken' }
                    ]}
                  >
                    <Input placeholder={`Ange namn på ${getDescriptionFromLocale(language.locale).toLowerCase()}`} />
                  </Form.Item>

                  <Form.Item
                    label="Beskrivning"
                    name={`description_${language.id}`}
                    rules={[
                      { max: 500, message: 'Beskrivning får inte vara längre än 500 tecken' }
                    ]}
                  >
                    <TextArea 
                      rows={3}
                      placeholder={`Ange beskrivning på ${getDescriptionFromLocale(language.locale).toLowerCase()}`}
                    />
                  </Form.Item>
                </TranslationSection>
              ))}

              <ButtonGroup>
                <Button onClick={handleBack}>
                  Avbryt
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<Save size={16} />}
                >
                  Spara ändringar
                </Button>
              </ButtonGroup>
            </Form>
          </StyledCard>
        </LeftColumn>

        <RightColumn>
          <StyledCard 
            title="Produkttilldelning"
            extra={
              <Button 
                style={{
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                }}
                type="primary"
                icon={<Import size={16} />}
                onClick={() => setSkuDialogVisible(true)}
                size="small"
              >
                Importera artikelnummer
              </Button>
            }
          >
            <ProductSelectView
              selectedProducts={selectedProducts}
              onProductsChange={setSelectedProducts}
              title="Tilldela produkter till produktgrupp"
              maxHeight={600}
              productGroupId={Number(id)}
              onProductUnassigned={(productId) => {
                // Refresh the data when a product is unassigned
                queryClient.invalidateQueries({ queryKey: ['product-group', id] });
              }}
            />
          </StyledCard>
        </RightColumn>
      </Container>
      
      {/* SKU Import Dialog */}
      {id && (
        <ProductGroupAssignSkuDialog
          visible={skuDialogVisible}
          onCancel={() => setSkuDialogVisible(false)}
          productGroupId={Number(id)}
          onSuccess={handleSkuImportSuccess}
        />
      )}
    </>
  );
};

export default ProductGroupEditView;
